import { Injectable } from "@angular/core";
import { Store, select } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, filter, take } from "rxjs";
import { UserInfoDto } from "src/app/common/dto/user-info";
import { EnvironmentType } from "src/app/common/enums/environmentType";
import { PLUGIN } from "src/app/common/enums/plugins";
import { SLOT } from "src/app/common/enums/slot";
import { AnalyticsUserDimensions } from "src/app/common/utils/analytics-user-dimensions";
import { AnyModuleType, CrmModule, toCrmModule } from "src/app/core/helpers/crm-modules";
import { trackEvent } from "src/app/ngrx/actions/analytics.actions";
import { RootState } from "src/app/ngrx/root-reducers";
import { selectRegionAgs } from "src/app/ngrx/selectors/app.selectors";
import { selectLaunchpadOverlay } from "src/app/ngrx/selectors/overlay.selectors";
import { selectCurrentSlot } from "src/app/ngrx/selectors/router.selectors";
import { SidebarService } from "src/app/shared/ui/sidebar/sidebar.service";
import { environment } from "src/environments/environment";
import { AnalyticsEventProps } from "../../analytics/interfaces";
import { ContentfulContentType } from "../../interfaces/contentful";
import { LaunchpadOverlay } from "../../interfaces/overlay";
import { takeLatestFrom } from "../../utils/ngrx";

const moduleTrackingNameMap: Record<CrmModule, string> = {
  // regulated modules:
  [CrmModule.Accounting]: "Abrechnungen und Verträge",
  [CrmModule.GridConnection]: "Energiedaten und Netzinformationen",
  [CrmModule.OutageMonitoring]: "Störungsmonitoring",
  [CrmModule.ThreeYearPlan]: "Dreijahresplanung und Bauabstimmung",
  [CrmModule.GridScenarios]: "Netzentwicklung und Szenarien",

  // marketplace modules:
  [CrmModule.Co2Balance]: "co2balance DL",
  [CrmModule.KEM]: "Kommunales Energiemanagement DL",
  [CrmModule.Noysee]: "Noysee DL",
  [CrmModule.ShoppingCart]: "Bestellkorb Öffentliche Beleuchtung DL",
  [CrmModule.StreetLighting]: "Straßenbeleuchtung DL",
  [CrmModule.Diginamic]: "Diginamic DL",
  [CrmModule.CampusOne]: "eCampusKommunal DL",
  [CrmModule.QuickCheck]: "Quick Check DL",
  [CrmModule.KWP]: "KWP DL"
};

export function getModuleTrackingName(module: AnyModuleType): string {
  const crmModule = toCrmModule(module);
  const trackingName = moduleTrackingNameMap[crmModule];
  if (!trackingName && environment.type !== EnvironmentType.PROD) {
    console.warn(`No tracking name found for module ${module}`);
  }
  return trackingName || module;
}

@Injectable({ providedIn: "root" })
export class AnalyticsService {
  private isActive$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private readonly store: Store<RootState>,
    private readonly translate: TranslateService,
    private readonly sidebar: SidebarService
  ) {
    this.initTrackAGS();
    this.initTrackSidebar();
  }

  public getModuleTrackingName(module: AnyModuleType): string {
    return getModuleTrackingName(module);
  }

  public activate(userInfo: UserInfoDto) {
    (window as any).activateMatomoAnalytics(environment.type, AnalyticsUserDimensions, userInfo);
    this.isActive$.next(true);
  }

  public deactivate() {
    this.isActive$.next(false);
    (window as any).deactivateMatomoAnalytics();
  }

  public trackEvent(props: AnalyticsEventProps): void {
    this.isActive$
      .pipe(
        filter((e) => Boolean(e)),
        take(1)
      )
      .subscribe(() => this.store.dispatch(trackEvent(props)));
  }

  public trackPanelEntry(module: SLOT, panelName: string): void {
    const panelNameTranslate = this.translate.instant(`TOOLTIP.panelName.${panelName}`);

    this.trackEvent({
      category: this.getModuleTrackingName(module),
      action: panelNameTranslate,
      name: panelNameTranslate
    });
  }

  public trackNavigateToSlotFromLaunchpad(slot: SLOT | PLUGIN): void {
    const _slot = takeLatestFrom(this.store, selectCurrentSlot);

    if (slot !== _slot) {
      const slotTranslate = this.getModuleTrackingName(slot);
      this.trackEvent({ category: slotTranslate, action: "Launchpad", name: slotTranslate });
    }
  }

  public trackNavigateToPluginFromLaunchpad(slot: SLOT | PLUGIN): void {
    const pluginTranslate = this.getModuleTrackingName(slot);
    this.trackEvent({ category: pluginTranslate, action: "Launchpad", name: pluginTranslate });
  }

  public trackStorytellerClick(slot: SLOT): void {
    this.trackEvent({
      category: this.getModuleTrackingName(slot),
      action: "Launchpad",
      name: "Storyteller"
    });
  }

  public trackGlossaryClickInHeader(): void {
    const glossaryTranslate = this.translate.instant("HEADER.helpMenu.glossary");
    this.trackEvent({
      category: `${this.translate.instant("HEADER.helpMenu.label")} ${this.getOverlayTranslate()}`,
      action: glossaryTranslate,
      name: glossaryTranslate
    });
  }

  public trackGlossaryTabClick(tabTitle: string): void {
    this.trackEvent({
      category: `${this.translate.instant("HEADER.helpMenu.label")} ${this.getOverlayTranslate()}`,
      action: this.translate.instant("HEADER.helpMenu.glossary"),
      name: this.translate.instant(`GLOSSARY_DIALOG.tabs.${tabTitle}`)
    });
  }

  public trackContactTabClick(tab: string, action?: string): void {
    this.trackEvent({
      category: `${this.translate.instant("HEADER.helpMenu.label")} ${this.getOverlayTranslate()}`,
      action: action || this.translate.instant("HEADER.helpMenu.support"),
      name: this.translate.instant(`CONTACT_DIALOG.tab.${tab}`)
    });
  }

  public trackOpenContactForm(from: string, category?: string): void {
    this.trackEvent({
      category: category || `${this.translate.instant("HEADER.helpMenu.label")} ${this.getOverlayTranslate()}`,
      action: from,
      name: this.translate.instant("CONTACT_DIALOG.title")
    });
  }

  public trackWebinarsClickInHeader(): void {
    const webinarsTranslate = this.translate.instant("HEADER.helpMenu.webinars");
    this.trackEvent({
      category: `${this.translate.instant("HEADER.helpMenu.label")} ${this.getOverlayTranslate()}`,
      action: webinarsTranslate,
      name: webinarsTranslate
    });
  }

  public trackWebinarsClick(name: string): void {
    this.trackEvent({
      category: `${this.translate.instant("HEADER.helpMenu.label")} ${this.getOverlayTranslate()}`,
      action: this.translate.instant("HEADER.helpMenu.webinars"),
      name
    });
  }

  public trackPolicyClickInHeader(): void {
    const policyTranslate = this.translate.instant("HEADER.accountMenu.policy");
    this.trackEvent({
      category: this.translate.instant("HEADER.accountMenu.label"),
      action: policyTranslate,
      name: policyTranslate
    });
  }

  public trackNotificationsClickInHeader(): void {
    const notificationsTranslate = this.translate.instant("HEADER.accountMenu.notificationSettings");
    this.trackEvent({
      category: this.translate.instant("HEADER.accountMenu.label"),
      action: notificationsTranslate,
      name: notificationsTranslate
    });
  }

  public trackNotificationTabClick(tabTitle: string): void {
    this.trackEvent({
      category: this.translate.instant("HEADER.accountMenu.label"),
      action: this.translate.instant("HEADER.accountMenu.notificationSettings"),
      name: `${this.translate.instant(tabTitle)} ${this.translate.instant("HEADER.accountMenu.notificationSettings")}`
    });
  }

  public trackOpenNewsClick(): void {
    this.trackEvent({
      category: this.translate.instant("HEADER.accountMenu.label"),
      action: "Launchpad"
    });
  }

  public trackOpenTeaserDialogClick(slot: SLOT): void {
    this.trackEvent({
      category: this.getModuleTrackingName(slot),
      action: "Launchpad",
      name: "Vertriebsteaser"
    });
  }

  public trackTeaserEmailClick(module: PLUGIN): void {
    this.trackEvent({
      category: "Vertriebsteaser",
      action: this.getModuleTrackingName(module),
      name: "Email"
    });
  }

  public trackTeaserFormSubmit(module: PLUGIN): void {
    this.trackEvent({
      category: this.getModuleTrackingName(module),
      action: "Vertriebsteaser",
      name: "Anfrage"
    });
  }

  public trackNewsTypeChange(contentType: ContentfulContentType): void {
    const contentTypeTranslate = this.translate.instant(`CONTENTFUL.NEWS.TOGGLES.${contentType}`);
    this.trackEvent({
      category: this.translate.instant("LAUNCHPAD.NON_MODULES.news.name"),
      action: contentTypeTranslate,
      name: contentTypeTranslate
    });
  }

  public trackNewsItemClick(contentType: ContentfulContentType, label: string): void {
    this.trackEvent({
      category: this.translate.instant("LAUNCHPAD.NON_MODULES.news.name"),
      action: this.translate.instant(`CONTENTFUL.NEWS.TOGGLES.${contentType}`),
      name: label
    });
  }

  public trackNewsActionClick(module: SLOT, action: string): void {
    this.trackEvent({
      category: this.getModuleTrackingName(module),
      action,
      name: this.translate.instant("LAUNCHPAD.NON_MODULES.news.name")
    });
  }

  public trackMapFilterChange(): void {
    const slot = takeLatestFrom(this.store, selectCurrentSlot);

    this.trackEvent({
      category: this.getModuleTrackingName(slot),
      action: this.translate.instant("TOOLTIP.panelName.mapLayers"),
      name: "Filter verändert"
    });
  }

  public trackKpAreaOpen(action: "Launchpad" | "Direktlink", overlay: LaunchpadOverlay): void {
    this.trackEvent({
      category: "KP area",
      action,
      name: { module: "Netzbetrieb", plugin: "Dienstleistung" }[overlay]
    });
  }

  public trackPngModuleOpen(): void {
    this.trackEvent({
      category: "Pachtnetzgesellschaften",
      action: "Zur Pachtnetz-Verwaltung",
      name: "Wechsel Verwaltungsbereich"
    });
  }

  public trackDirectLink(module: AnyModuleType): void {
    if (!module) {
      return;
    }

    const name = getModuleTrackingName(module);

    if (name === module) {
      // if the slotId is not a CrmModule or SLOT, we don't have a tracking name for it
      return;
    }

    this.trackEvent({
      action: "Direktlink",
      category: name,
      name
    });
  }

  public scanForForms() {
    (window as any).scanForForms();
  }

  public scanForMedia() {
    (window as any).scanForMedia();
  }

  private getOverlayTranslate(): string {
    return takeLatestFrom(this.store, selectLaunchpadOverlay) === "module" ? "" : "DL";
  }

  /**
   * For the report of users per municipality we need to track the ags of the municipality.
   */
  private initTrackAGS(): void {
    this.store.pipe(select(selectRegionAgs), filter(Boolean)).subscribe((ags) => {
      this.trackEvent({
        category: "report",
        action: "ags",
        name: ags
      });
    });
  }

  private initTrackSidebar(): void {
    this.sidebar.stateEvent$.pipe(filter(({ opened }) => !!opened.length)).subscribe(({ opened }) => {
      const module = takeLatestFrom(this.store, selectCurrentSlot) as SLOT;

      this.trackEvent({
        category: this.getModuleTrackingName(module),
        action: this.translate.instant(`TOOLTIP.panelName.${opened[0]}`),
        name: this.translate.instant(`TOOLTIP.panelName.${opened[0]}`)
      });
    });
  }
}
